import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { ImpalaLinkedService, impalaAuthTypes } from 'app/model/linkedservice/impala.linkedservice';
import {
    passwordOrAKVtoSecretBase,
    secretBaseToPasswordOrAKV
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ImpalaLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                host: '*',
                port: '*',
                authenticationType: '*',
                username: '*',
                password: 'secret',
                enableSsl: '*',
                trustedCertPath: '*',
                thriftTransportProtocol: '*',
                useSystemTrustStore: '*',
                allowHostNameCNMismatch: '*',
                allowSelfSignedServerCert: '*',
                encryptedCredential: '*',
                enableServerCertificateValidation: '*',
            }
        }
    }
})
export class ImpalaLinkedServiceAdapter extends LinkedServiceAdapter<ImpalaLinkedService> {
    protected modelConstructor(): new () => ImpalaLinkedService {
        return ImpalaLinkedService;
    }

    protected model(): IModel {
        return ImpalaLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: ImpalaLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.ImpalaLinkedService;
        properties.type = Metamodel.modelFor(ImpalaLinkedService).name;
        const password: Model.SecretBase =
            linkedService.authenticationType === impalaAuthTypes.UsernameAndPassword && linkedService.password
                ? passwordOrAKVtoSecretBase(linkedService.password, this.context)
                : undefined;
        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            properties.typeProperties = {
                ...this._commonProperties(linkedService),
                password,
                thriftTransportProtocol: linkedService.thriftTransportProtocol,
                enableServerCertificateValidation: linkedService.enableSsl ? linkedService.enableServerCertificateValidation : undefined,
            };
        } else {
            properties.typeProperties = {
                ...this._commonProperties(linkedService),
                password,
                trustedCertPath: linkedService.enableSsl ? linkedService.trustedCertPath : undefined,
                useSystemTrustStore: linkedService.enableSsl ? linkedService.useSystemTrustStore : undefined,
                allowHostNameCNMismatch: linkedService.enableSsl ? linkedService.allowHostNameCNMismatch : undefined,
                allowSelfSignedServerCert: linkedService.enableSsl ? linkedService.allowSelfSignedServerCert : undefined
            };
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: ImpalaLinkedService
    ): Promise<ImpalaLinkedService> {
        const properties = linkedServiceResource.properties as Model.ImpalaLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.ImpalaLinkedServiceTypeProperties = properties.typeProperties;
        linkedService.version = properties.version === CONNECTOR_VERSION20 ? driverVersions.v2 : driverVersions.v1;
        const isV2 = properties.version === CONNECTOR_VERSION20;
        linkedService.isLegacyConnector = !isV2;
        linkedService.host = typeProperties.host;
        linkedService.port = typeProperties.port;
        linkedService.authenticationType = typeProperties.authenticationType;
        linkedService.username = typeProperties.username;
        linkedService.password = secretBaseToPasswordOrAKV(
            typeProperties.password,
            typeProperties.authenticationType === impalaAuthTypes.UsernameAndPassword,
            this.context
        );
        linkedService.enableSsl = typeProperties.enableSsl;
        //v1
        linkedService.trustedCertPath = isV2 ? undefined : typeProperties.trustedCertPath;
        linkedService.useSystemTrustStore = isV2 ? undefined : typeProperties.useSystemTrustStore;
        linkedService.allowHostNameCNMismatch = isV2 ? undefined :typeProperties.allowHostNameCNMismatch;
        linkedService.allowSelfSignedServerCert = isV2 ? undefined :typeProperties.allowSelfSignedServerCert;
        //v2
        linkedService.enableServerCertificateValidation = isV2 ? typeProperties.enableServerCertificateValidation : undefined;
        linkedService.thriftTransportProtocol = isV2 ? typeProperties.thriftTransportProtocol : undefined;
        return linkedService;
    }

    private _commonProperties(linkedService: ImpalaLinkedService): Model.ImpalaLinkedServiceTypeProperties {
        return {
            host: linkedService.host,
            port: linkedService.port,
            authenticationType: linkedService.authenticationType,
            username:
                linkedService.authenticationType !== impalaAuthTypes.Anonymous ? linkedService.username : undefined,
            enableSsl: linkedService.enableSsl
        };
    }
}
